import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Stack,
  Paper,
  LinearProgress,
  Avatar,
  Divider,
} from "@mui/material";
import moment from "moment";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

const deliveryStatuses = [
  { value: 1, label: "En route", category: "pickup", icon: "fa fa-car" },
  {
    value: 2,
    label: "At Pickup",
    category: "pickup",
    icon: "fa fa-map-marker",
  },
  {
    value: 3,
    label: "Loading Started",
    category: "loading",
    icon: "fa fa-play-circle",
  },
  {
    value: 4,
    label: "Loading Completed",
    category: "loading",
    icon: "fa fa-check-circle",
  },
  { value: 5, label: "In Transit", category: "transit", icon: "fa fa-truck" },
  {
    value: 6,
    label: "At Delivery",
    category: "delivery",
    icon: "fa fa-warehouse",
  },
  {
    value: 7,
    label: "Unloading Started",
    category: "delivery",
    icon: "fa fa-play-circle",
  },
  {
    value: 8,
    label: "Unloading Completed",
    category: "delivery",
    icon: "fa fa-check-circle",
  },
  {
    value: 9,
    label: "Detention Begin",
    category: "special",
    icon: "fa fa-clock-o",
  },
  {
    value: 10,
    label: "Detention Ended",
    category: "special",
    icon: "fa fa-clock-o",
  },
  {
    value: 11,
    label: "Pickup Completed",
    category: "pickup",
    icon: "fa fa-check-circle",
  },
  {
    value: 12,
    label: "Delivered",
    category: "delivery",
    icon: "fa fa-shipping-fast",
  },
  {
    value: 13,
    label: "Completed",
    category: "final",
    icon: "fa fa-check-circle",
  },
];

export const LoadStatusTracker = ({ data }: any) => {
  // Get status details by value
  const getStatusDetails = (statusValue: any) => {
    return (
      deliveryStatuses.find((status) => status.value === statusValue) ||
      deliveryStatuses.find(
        (status) => status.label.toLowerCase() === statusValue?.toLowerCase(),
      )
    );
  };

  // Get status color based on category
  const getStatusColor = (category: any) => {
    switch (category) {
      case "pickup":
        return "info";
      case "loading":
        return "warning";
      case "transit":
        return "primary";
      case "delivery":
        return "secondary";
      case "special":
        return "error";
      case "final":
        return "success";
      default:
        return "default";
    }
  };

  // Calculate progress percentage
  const getProgressPercentage = (currentStatus: any) => {
    const statusDetails = getStatusDetails(currentStatus);
    if (!statusDetails) return 0;
    return (statusDetails.value / 13) * 100;
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: "100%", sm: 600, md: 700 },
        margin: "auto",
      }}
    >
      <Accordion
        sx={{
          boxShadow: "none",
          marginBottom: 0,
        }}
      >
        <AccordionSummary
          expandIcon={<i className="fa-solid fa-angle-down text-gray-700"></i>}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            borderBottom: "1px solid #e7e7e7",
            padding: 0,
            justifyContent: "start",
            "& .MuiAccordionSummary-content": {
              flexGrow: "0",
              padding: "0 5px 0px 0",
            },
          }}
        >
          <Typography component="span" className="text-gray-700">
            {" "}
            🚛 Delivery Status Tracker
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            pl: 0,
            pr: 0,
          }}
        >
          <Stack spacing={2}>
            {data?.status.map((item: any, index: any) => {
              const statusDetails = getStatusDetails(item.status);
              const progress = getProgressPercentage(item.status);

              return (
                <Card
                  key={index}
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      elevation: 4,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {/* Header with Progress */}
                  <Paper
                    elevation={0}
                    sx={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      p: { xs: 1.5, sm: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                      >
                        LOAD STATUS #{index + 1}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.9,
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                      >
                        {Math.round(progress)}% Complete
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "rgba(255,255,255,0.3)",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#4caf50",
                        },
                      }}
                    />
                  </Paper>

                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    {/* Current Status */}
                    <Stack direction="row" justifyContent="space-between">
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          gutterBottom
                          display="block"
                          fontWeight="medium"
                        >
                          Current Status
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: `${getStatusColor(statusDetails?.category)}.light`,
                              width: { xs: 32, sm: 36 },
                              height: { xs: 32, sm: 36 },
                            }}
                          >
                            <i
                              className={statusDetails?.icon || "fa fa-circle"}
                              style={{ fontSize: "14px" }}
                            />
                          </Avatar>
                          <Box>
                            <Chip
                              label={statusDetails?.label || item.status}
                              color={getStatusColor(statusDetails?.category)}
                              variant="filled"
                              size="small"
                              sx={{
                                fontWeight: "bold",
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      {/* Timeline Information */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "info.light",
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                          }}
                        >
                          <i
                            className="fa fa-clock-o"
                            style={{ fontSize: "12px" }}
                          />
                        </Avatar>
                        <Box flex={1}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            display="block"
                          >
                            Status Updated
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                          >
                            {moment(item.createdAt).format(
                              "MMM DD, YYYY hh:mm A",
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {moment(item.createdAt).fromNow()}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
